import { useCallback, useEffect, useMemo, useState } from "react";
import { listRows, updateRow } from "@/lib/adminApi";
import { AUDIT_CHECKLIST, AUDIT_STATUS, metaFor, optionsFrom, type SafetyAudit } from "@/lib/adminModules";
import {
  DataTable,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  fieldInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatDate } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

export default function AdminSafetyAudits() {
  const [rows, setRows] = useState<SafetyAudit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<SafetyAudit>("safety_audits", "farm_name"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load safety audits.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id: number, patch: Partial<SafetyAudit>) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("safety_audits", id, patch);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const toggleCheck = async (audit: SafetyAudit, key: string) => {
    const checklist = { ...(audit.checklist || {}) };
    checklist[key] = checklist[key] === "pass" ? "fail" : "pass";
    const fails = Object.values(checklist).filter((value) => value === "fail").length;
    setRows((list) =>
      list.map((row) => (row.id === audit.id ? { ...row, checklist, critical_issues: fails } : row)),
    );
    try {
      await updateRow("safety_audits", audit.id, { checklist, critical_issues: fails });
    } catch {
      setError("We couldn't save the checklist. Please try again.");
      load();
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (!query) return true;
      return row.farm_name.toLowerCase().includes(query);
    });
  }, [rows, search, statusFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;

  const columns: Column<SafetyAudit>[] = [
    {
      key: "farm",
      header: "Farm",
      primary: true,
      render: (row) => (
        <span className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.farm_name}</span>
      ),
    },
    {
      key: "last",
      header: "Last audit",
      render: (row) => <span className="text-xs text-foreground-500 whitespace-nowrap">{formatDate(row.last_audit)}</span>,
    },
    {
      key: "next",
      header: "Next audit",
      render: (row) => <span className="text-xs text-foreground-500 whitespace-nowrap">{formatDate(row.next_audit)}</span>,
    },
    {
      key: "score",
      header: "Score",
      render: (row) => <span className="text-sm font-semibold text-foreground-800">{row.score}</span>,
    },
    {
      key: "critical",
      header: "Critical issues",
      render: (row) =>
        row.critical_issues > 0 ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent-900">
            <i className="ri-alert-line"></i> {row.critical_issues}
          </span>
        ) : (
          <span className="text-xs text-foreground-500">None</span>
        ),
    },
    { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(AUDIT_STATUS, row.status)} /> },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Farm Network"
        title="Safety Audits"
        subtitle="A 12-point safety checklist for every farm — entry, machinery, animals, chemicals, first aid and more."
      />

      <StatCards
        stats={[
          { label: "Farms audited", value: rows.length, icon: "ri-shield-flash-line", tone: "primary" },
          {
            label: "Passed",
            value: rows.filter((row) => row.status === "passed").length,
            icon: "ri-checkbox-circle-line",
            tone: "primary",
          },
          {
            label: "Corrective action",
            value: rows.filter((row) => row.status === "corrective").length,
            icon: "ri-tools-line",
            tone: "accent",
          },
          {
            label: "Audits due",
            value: rows.filter((row) => row.status === "due" || row.status === "overdue").length,
            icon: "ri-calendar-line",
            tone: "secondary",
          },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by farm name…" />
          <Select
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[{ value: "all", label: "All statuses" }, ...optionsFrom(AUDIT_STATUS)]}
          />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">
            {filtered.length} audits
          </span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading safety audits…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ri-shield-flash-line"
            title={rows.length === 0 ? "No audits yet" : "No audits match"}
            message="Start a safety audit to record a farm's compliance."
          />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.farm_name || "Safety audit"}
        subtitle="12-point safety checklist"
        footer={
          selected ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => update(selected.id, { status: "passed", last_audit: new Date().toISOString().slice(0, 10) })}
                className="inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-check-double-line"></i> Submit &amp; pass
              </button>
              <button
                type="button"
                onClick={() => update(selected.id, { status: "corrective" })}
                className="inline-flex items-center gap-2 border border-background-300 text-foreground-800 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-tools-line"></i> Request corrective
              </button>
            </div>
          ) : null
        }
      >
        {selected ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill {...metaFor(AUDIT_STATUS, selected.status)} />
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-secondary-700 bg-secondary-100 border border-secondary-200 px-2.5 py-1 rounded-full">
                Score {selected.score}
              </span>
              {selected.critical_issues > 0 ? (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent-900 bg-accent-100 border border-accent-300 px-2.5 py-1 rounded-full">
                  <i className="ri-alert-line"></i> {selected.critical_issues} critical
                </span>
              ) : null}
            </div>

            <Field label="Checklist">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {AUDIT_CHECKLIST.map((item) => {
                  const value = (selected.checklist || {})[item.key];
                  const pass = value !== "fail";
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => toggleCheck(selected, item.key)}
                      className={`flex items-center justify-between gap-2 rounded-md border px-3 py-2.5 text-sm transition-colors cursor-pointer ${
                        pass
                          ? "bg-primary-100 border-primary-300 text-primary-800"
                          : "bg-accent-100 border-accent-300 text-accent-900"
                      }`}
                    >
                      <span className="font-medium">{item.label}</span>
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold">
                        <i className={pass ? "ri-checkbox-circle-fill" : "ri-close-circle-fill"}></i>
                        {pass ? "Pass" : "Fail"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Last audit">
                <input
                  type="date"
                  value={(selected.last_audit || "").slice(0, 10)}
                  onChange={(event) => update(selected.id, { last_audit: event.target.value })}
                  className={fieldInput}
                />
              </Field>
              <Field label="Next audit">
                <input
                  type="date"
                  value={(selected.next_audit || "").slice(0, 10)}
                  onChange={(event) => update(selected.id, { next_audit: event.target.value })}
                  className={fieldInput}
                />
              </Field>
            </div>

            <Field label="Status">
              <select
                value={selected.status}
                onChange={(event) => update(selected.id, { status: event.target.value })}
                className={fieldInput}
              >
                {optionsFrom(AUDIT_STATUS).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Notes">
              <textarea
                rows={3}
                value={selected.notes || ""}
                onChange={(event) =>
                  setRows((list) =>
                    list.map((row) => (row.id === selected.id ? { ...row, notes: event.target.value } : row)),
                  )
                }
                onBlur={() => update(selected.id, { notes: selected.notes })}
                placeholder="Corrective actions, deadlines, evidence…"
                className="w-full bg-background-100 border border-background-300 rounded-md px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
              ></textarea>
            </Field>
          </>
        ) : null}
      </Drawer>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Tick a checkpoint to toggle pass / fail — the critical count updates live.
      </p>
      {node}
    </div>
  );
}